import { t } from "i18next";

import { readState } from "@/__main__/app-state.mjs";
import type { BattlesEvent } from "@/feature-battles/model-events.mjs";
import type { ParticipantInfo } from "@/feature-battles/model-participants-info.mjs";
import type { GameResult } from "@/feature-battles/model-post-match-result.mjs";
import type { UserOptIn } from "@/feature-battles/model-user-opt-in.mjs";
import type { UserPastEvents } from "@/feature-battles/model-user-past-event.mjs";
import { GAME_SYMBOL_APEX } from "@/game-apex/definition-symbol.mjs";
import { GAME_SYMBOL_CS2 } from "@/game-csgo/definition-symbol.mjs";
import { GAME_SYMBOL_EFT } from "@/game-eft/definition-symbol.mjs";
import { GAME_SYMBOL_FORTNITE } from "@/game-fortnite/definition-symbol.mjs";
import { GAME_SYMBOL_LOL } from "@/game-lol/definition-symbol.mjs";
import { GAME_SYMBOL_TFT } from "@/game-tft/definition-symbol.mjs";
import { GAME_SYMBOL_UNKNOWN } from "@/game-unknown/definition-symbol.mjs";
import { GAME_SYMBOL_VAL } from "@/game-val/definition-symbol.mjs";
import BattlesIcon from "@/inline-assets/nav-battles.svg";
import symbolName from "@/util/symbol-name.mjs";

export const BATTLES_NAME = "battles";
export const HUB_SYMBOL_BATTLES = symbolName("hub-battles");

export const LEADERBOARD_PAGE_SIZE = 25;
export const PAST_EVENT_PAGE_SIZE = 20;

// Backoff time for generally unchanging event data (event list/details)
export const EVENT_STORAGE_TIME = 2 * 60 * 60 * 1000;
// Backoff time for constantly changing event data (leaderboard, points)
export const UTD_STORAGE_TIME = 60 * 1000;

export const HANDLED_GAMES = {
  [GAME_SYMBOL_LOL]: true,
  [GAME_SYMBOL_VAL]: false,
  [GAME_SYMBOL_TFT]: false,
  [GAME_SYMBOL_APEX]: false,
  [GAME_SYMBOL_CS2]: false,
  [GAME_SYMBOL_EFT]: false,
  [GAME_SYMBOL_UNKNOWN]: false,
  [GAME_SYMBOL_FORTNITE]: true,
};

export const MainNavBattlesLink = {
  key: HUB_SYMBOL_BATTLES,
  Icon: BattlesIcon,
  translation: ["battles:battles", "Battles"],
  supportedGames: [
    /* ALL */ undefined,
    ...Object.getOwnPropertySymbols(HANDLED_GAMES).filter(
      (x) => HANDLED_GAMES[x],
    ),
  ],
};

export const DEFAULT_OVERLAY_LOGO = "blitz/battles/overlay-default-logo.png";
export const DEFAULT_OVERLAY_ICON = "blitz/battles/overlay-default-icon.png";

export const ALL_QUEUES = "ALL";
export const UNHANDLED_QUEUE = "UNHANDLED_QUEUE";
export const ALL_MAPS = "ALL";
export const UNHANDLED_MAP = "UNHANDLED_MAP";

// TODO: no enum
export const enum EventStatus {
  PRELIVE = "prelive",
  LIVE = "live",
  ENDED = "ended",
}

// Analytics events
export const EVENT_BATTLES_CLICK = "EVENT_BATTLES_CLICK";
export const CLICK_ACTIONS = {
  close: "close",
  details: "see details",
  join: "join event",
};

export const EVENT_TRACK_BATTLES_AD_IMPRESSION =
  "EVENT_TRACK_BATTLES_AD_IMPRESSION";
export const IMPRESSION_TARGETS = {
  bannerProfile: "profile banner",
  bannerPostMatch: "post match banner",
  bannerEventAdSponsor: "event sponsor banner ad",
  cardFeaturedEventInfo: "featured event info card",
  nativeAdSponsor: "sponsor native ad",
  tableBrowseEvents: "browse events table row",
  tablePastEvents: "past events table row",
  modalAccountSelect: "account select modal",
  loadingScreen: "loading screen",

  // Deprecated
  // modalPreLive: "starts soon modal",
  // modalLive: "now live modal",
  // cardEventInfo: "event info card",
  // notificationTop: "top right notification",
  // notificationSnackbar: "snackbar notification",
};

export const BREADCRUMBS = {
  title: ["battles:battles", "Battles"],
  maxCrumbs: 3,
  baseUrl: BATTLES_NAME,
  getLocalizedCrumb({ pathChunk, currentPath }) {
    if (isNaN(pathChunk)) return null;
    const event = readState.battles.eventMap[pathChunk];
    if (event) return { title: event.displayName, href: currentPath };
    return { title: t("battles:event", "Battles Event"), href: currentPath };
  },
};

export const REF_PARAM = "ref=blitz-app";

declare module "@/__main__/app-state.mjs" {
  interface FeatureState {
    battles: {
      /// Map by eventId of all events
      eventMap: Record<string, BattlesEvent>;
      /// Map by eventId of all current events the user has opted into.
      userOptIns: Record<string, UserOptIn>;
      /// Map by eventId of info abt game a user played while an event was running.
      gameResults: Record<string, GameResult>;
      /// Map by eventId of leaderboard & participantCount.
      eventParticipantInfo: Record<string, ParticipantInfo>;
      /// List of event modals dismissed by user.
      dismissedEvents: {
        live: string[];
        prelive: string[];
        /// A new tag was shown in the main nav
        nav: string[];
      };
      /// Map by game of default account id to use when opting in.
      defaultAccount: Record<string, string>;
      /// Used by your events
      pastEvents?: UserPastEvents;
    };
  }

  interface VolatileState {
    hasCleanedEvents?: boolean;
    pastEventSort?: string;
    previousPastEventSort?: {
      ASC: UserPastEvents;
      DESC: UserPastEvents;
    };
  }
}
